import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { AIInputWithLoading } from "@/components/ui/ai-input-with-loading";
import { Typewriter } from "@/components/ui/typewriter";
import { useChat } from "ai/react";
import { Button } from "@/components/ui/button";

// Add type definitions
interface Annotation {
  context?: {
    kbContext?: any[];
    ticketsContext?: any[];
    memoriesContext?: any[];
    snippetsContext?: any[];
  };
  messageIndex?: number;
  step?: string;
  description?: { title: string; text: string };
  content?: any;
}

const capitalizeString = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

import { MessageComponent } from "./Message";
import { MessageLoading } from "@/components/ui/loading";
import { TimelineContent, TimelineItem } from "@/components/ui/timeline";
import {
  TimelineDot,
  TimelineHeading,
  TimelineLine,
} from "@/components/ui/timeline";
import { Message } from "ai";
import { BadResponsePage } from "./parts/bad-response-page";
import { useRetryResponse } from "./parts/api/retry";
import { params } from "@/params";

const processMapper = {
  reading_message: "Going over your message",
  looking_for_sources: "Looking for relevant sources in the help center",
  potential_questions: "Searching",
  reviewing_relevant_sources: "Reviewing relevant sources that I found",
  looking_for_the_answer: "Searching the correct answer",
  drafting_response: "Drafting initial Response",
  typing: "Typing...",
  done: "Finished and sent response",
  none: "",
};

export const YoavosPlayground = () => {
  const [step, setStep] = useState<{ step: string; metadata: any } | null>(
    null
  );
  const [chatId, setChatId] = useState(String(new Date().valueOf()));
  const searchParams = new URLSearchParams(window.location.search);
  const tenant = searchParams.get("t");
  const feedbackKey = `quack-${tenant}-positiveFeedback`;
  const feedbackCount = Number(localStorage.getItem(feedbackKey) || 0);
  const [badResponseConversation, setBadResponseConversation] = useState<
    Message[]
  >([]);

  const bottomRef = useRef<HTMLDivElement>(null);
  const bottomStepsRef = useRef<HTMLDivElement>(null);

  const { messages, isLoading, setMessages, append, data, reload } = useChat({
    id: chatId,
    api: `${params.AI_URL}/api/simulate-reply/tickets?stream=true`,
    body: {
      tenantId: tenant,
      options: {
        channel: "tickets",
        tone: "casual",
      },
      isDemo: window.location.href.includes("demo"),
    },
    onFinish: () => {
      setStep(null);
    },
  });

  const steps = useMemo(() => {
    const filteredSteps = (data?.filter((d: any) => d.step !== "typing") ||
      []) as Annotation[];
    const lastMessageSteps =
      filteredSteps.length > 0
        ? filteredSteps.filter(
            (d: Annotation) =>
              d.messageIndex ===
              filteredSteps[filteredSteps.length - 1]?.messageIndex
          )
        : [];
    return lastMessageSteps;
  }, [data]);

  useEffect(() => {
    setTimeout(() => {
      setMessages((prev: Message[]) => {
        return [
          {
            id: "intro",
            content:
              feedbackCount < 6
                ? `Hey, I'm Quack. I'm really excited about the opportunity to work together! I'm kind of new to ${capitalizeString(
                    tenant ?? ""
                  )} ${
                    feedbackCount > 0
                      ? `although I already got ${feedbackCount} good ${
                          feedbackCount === 1 ? "answer" : "answers"
                        } `
                      : ""
                  }, so it might take me a bit longer than usual to find the best answer, but let's give it a try. `
                : `Hey again, seems that we on track, can't wait to start working together officaly, until than keep testing me`,
            role: "assistant",
          },
        ];
      });
    }, 300);
  }, [chatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [messages]);

  const handleBadFeedback = () => {
    setBadResponseConversation(messages);
  };

  const context = useMemo(() => {
    let context = {
      kb: [] as any[],
      tickets: [] as any[],
      memories: [] as any[],
      snippets: [] as any[],
    };

    if (messages.length > 0) {
      const message = messages[messages.length - 1];

      if (message.annotations && message.annotations.length > 0) {
        const annotationArray = Array.isArray(message.annotations[0])
          ? message.annotations[0]
          : message.annotations;

        for (const annotation of annotationArray) {
          const typedAnnotation = annotation as Annotation;
          if (typedAnnotation.context) {
            if (
              typedAnnotation.context.kbContext &&
              typedAnnotation.context.kbContext.length > 0
            ) {
              context.kb = typedAnnotation.context.kbContext;
            }
            if (
              typedAnnotation.context.ticketsContext &&
              typedAnnotation.context.ticketsContext.length > 0
            ) {
              context.tickets = typedAnnotation.context.ticketsContext;
            }
            if (
              typedAnnotation.context.memoriesContext &&
              typedAnnotation.context.memoriesContext.length > 0
            ) {
              context.memories = typedAnnotation.context.memoriesContext;
            }
            if (
              typedAnnotation.context.snippetsContext &&
              typedAnnotation.context.snippetsContext.length > 0
            ) {
              context.snippets = typedAnnotation.context.snippetsContext;
            }
          }
        }
      }
    }

    return context;
  }, [messages]);

  const { retryWithSuggestions } = useRetryResponse();
  const handleToolSuggestion = async ({
    suggestedMemories,
    suggestedSnippets,
  }: {
    suggestedMemories: any[];
    suggestedSnippets: any[];
  }) => {
    const messagesWithoutLast = messages.slice(0, -1);

    const responseText = await retryWithSuggestions({
      suggestedMemories,
      suggestedSnippets,
      tenant: tenant ?? "",
      messagesWithoutLast,
    });

    if (responseText) {
    }

    return responseText;
  };

  const handleReset = () => {
    setChatId(String(new Date().valueOf()));
    setMessages([]);
    setStep(null);
    setBadResponseConversation([]);
    // Force a reload with a new chat ID to stop any ongoing requests
    reload();
  };

  if (badResponseConversation.length > 0) {
    return (
      <div className="flex flex-col grow relative">
        <div className="absolute top-4 right-4 z-10">
          <Button variant="outline" onClick={handleReset} className="text-sm">
            Reset Chat
          </Button>
        </div>
        <BadResponsePage
          conversation={badResponseConversation}
          context={context}
          handleToolSuggestion={handleToolSuggestion}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col grow relative">
      <div className="absolute top-4 right-4 z-10">
        <Button variant="outline" onClick={handleReset} className="text-sm">
          Reset Chat
        </Button>
      </div>
      <section className=" flex flex-grow h-2">
        <div className="flex grow">
          <div className=" flex flex-grow flex-col pt-3 border-r justify-center items-center max-w-2xl">
            <div className=" max-w-lg flex-grow flex flex-col w-full">
              <div
                id="messages"
                className="flex flex-col flex-grow gap-2 overflow-y-scroll h-3"
              >
                {messages.map((m: Message, index: number) => {
                  return (
                    <MessageComponent
                      m={m}
                      key={m.content}
                      index={index}
                      handleBadFeedback={handleBadFeedback}
                    />
                  );
                })}
                {isLoading && <MessageLoading />}
                <div className="h-10 min-h-4 mt-4" ref={bottomRef}></div>
              </div>
            </div>

            <AIInputWithLoading
              onSubmit={(value) => {
                append({ content: value, role: "user", id: uuid() });
              }}
              isLoading={isLoading}
              loadingDuration={3000}
              thinkingDuration={2000}
              placeholder="Ask me a question"
              step={
                processMapper?.[
                  (step?.step as keyof typeof processMapper) ?? "none"
                ] ?? ""
              }
              stepMetadata={step?.metadata}
              messages={messages.length}
            />
          </div>
          <div className="flex flex-col items-baseline overflow-y-scroll grow h-full max-w-96 p-3">
            {(
              steps as {
                step: string;
                description: { title: string; text: string };
                content: any;
              }[]
            )
              ?.filter((l) => l.step !== "typing")
              ?.map((annotation, i) => {
                return (
                  <TimelineItem
                    status="default"
                    className="before:content-[''] list-none"
                  >
                    <TimelineHeading>
                      {
                        processMapper[
                          annotation.step as keyof typeof processMapper
                        ]
                      }
                    </TimelineHeading>
                    <TimelineDot
                      status={
                        i ==
                        (steps as any[])?.filter((l) => l.step !== "typing")
                          .length -
                          1
                          ? "current"
                          : "done"
                      }
                    />
                    <TimelineLine done />
                    <TimelineContent className=" whitespace-break-spaces text-xs">
                      {annotation?.description?.title}
                      {annotation?.content &&
                        Object.keys(annotation.content).length > 0 && (
                          <div className="mt-1 text-xs text-gray-500">
                            {annotation.step === "reading_sources" && (
                              <div>
                                {" "}
                                <div>Reading:</div>
                                <div>{annotation.description.text}</div>
                              </div>
                            )}
                            {annotation.step === "reviewing_resources" && (
                              <div>
                                <div>Reviewing:</div>
                                {Object.entries(annotation.content).map(
                                  ([sourceType, sources]) => (
                                    <div key={sourceType} className="ml-2">
                                      <div className="font-medium">
                                        {sourceType === "quack-tickets"
                                          ? "Tickets:"
                                          : sourceType === "quack-kb"
                                          ? "Knowledge Base:"
                                          : sourceType === "quack-memory"
                                          ? "Memories:"
                                          : sourceType === "quack-snippet"
                                          ? "Snippets:"
                                          : sourceType}
                                      </div>
                                      {Array.isArray(sources) &&
                                      sources.length > 0 ? (
                                        sources.map(
                                          (source: any, idx: number) => (
                                            <div key={idx} className="ml-2">
                                              {source.question ||
                                                source.title ||
                                                source.text ||
                                                `Source ${idx + 1}`}
                                            </div>
                                          )
                                        )
                                      ) : (
                                        <div className="ml-2">
                                          No sources found
                                        </div>
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        )}
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            <div ref={bottomStepsRef} />
          </div>
        </div>
      </section>
      <footer className=" flex  justify-between w-full p-4 text-sm border-t">
        <div className=" md:text-4xl lg:text-5xl sm:text-3xl text-2xl flex flex-row items-start justify-start bg-background font-normal overflow-hidden ">
          <p className="whitespace-pre-wrap">
            <span>{"Quack AI revolutinizng the way support teams "}</span>
            <Typewriter
              text={[
                "manage their knowledge",
                "delight their customers",
                "assure quality",
                "automate resolution",
                "doing real impact",
              ]}
              speed={70}
              className="text-yellow-500"
              waitTime={1500}
              deleteSpeed={40}
              cursorChar={"_"}
            />
          </p>
        </div>
        <div>This playground build with 💛 & 🧠 by Quack AI team.</div>
      </footer>
    </div>
  );
};
